import { createApiHooks, useGenericGet, useGenericPost } from '../../../../../hooks/use-api.hook';
import { apiClient } from '../../../../../services/api-client.service';
import type {
  EmailConfiguration,
  EmailConfigurationCreate,
  EmailConfigurationGetFiltersResponse,
  EmailConfigurationGetResponse,
  EmailConfigurationUpdate,
} from '../types/emailConfiguration.types';
import type { UseListParams } from '../../../../../types/pagination.types';
import { useMemo } from 'react';

const useEmailConfigurationBasePath = () => {
  return useMemo(() => {
    return `/v1/sysadmin/EmailConfigurations`;
  }, []);
};

export const useEmailConfigurationHooks = () => {
  const emailConfigurationBasePath = useEmailConfigurationBasePath();

  return createApiHooks<
    EmailConfiguration,
    EmailConfigurationGetResponse,
    EmailConfigurationCreate,
    EmailConfigurationUpdate
  >({
    basePath: emailConfigurationBasePath,
    queryKey: ['EmailConfigurations'],
    apiClient,
  });
};

export const useEmailConfigurationGetFilters = (queryOptions?: any) => {
  const emailConfigurationBasePath = useEmailConfigurationBasePath();
  return useGenericGet<EmailConfigurationGetFiltersResponse>(
    `${emailConfigurationBasePath}/filters`,
    ['EmailConfigurations', 'filters'],
    apiClient,
    queryOptions
  );
};

// Create a custom hook that returns all the emailConfiguration hooks
export const useEmailConfigurationApi = () => {
  const emailConfigurationHooks = useEmailConfigurationHooks();

  return {
    useGet: emailConfigurationHooks.useGet,
    useCreate: emailConfigurationHooks.useCreate,
    useUpdate: emailConfigurationHooks.useUpdate,
    useDelete: emailConfigurationHooks.useDelete,
    useList: (params: UseListParams) => emailConfigurationHooks.useList(params),
  };
};

// Export individual hooks for backward compatibility
export const useEmailConfigurationGet = (id: string | number, queryOptions?: any) => {
  const { useGet } = useEmailConfigurationApi();
  return useGet(id, queryOptions);
};

export const useEmailConfigurationCreate = (mutationOptions?: any) => {
  const { useCreate } = useEmailConfigurationApi();
  return useCreate(mutationOptions);
};

export const useEmailConfigurationUpdate = (mutationOptions?: any) => {
  const { useUpdate } = useEmailConfigurationApi();
  return useUpdate(mutationOptions);
};

export const useEmailConfigurationDelete = (mutationOptions?: any) => {
  const { useDelete } = useEmailConfigurationApi();
  return useDelete(mutationOptions);
};

export const useEmailConfigurationList = (params: UseListParams) => {
  const { useList } = useEmailConfigurationApi();
  return useList(params);
};

export const useEmailConfigurationTest = (mutationOptions?: any) => {
  const emailConfigurationBasePath = useEmailConfigurationBasePath();

  return useGenericPost(
    `${emailConfigurationBasePath}/test`,
    ['EmailConfigurations', 'test'],
    apiClient,
    mutationOptions
  );
};
